// HD API endpoints remapped for ZX monolith.
//
// All endpoints share the /api/zxdata/ prefix:
//   api/zxdata/hd-*   — ZX HDAPI (HDUser JWT auth), previously at /hdapi/
//   api/zxdata/*      — HD-specific endpoints (accounts, analytics, access, etc.)
//
// NOTE: COMPANY, GENERIC_COMPANY, CONTRARY_REPORTS, and SACRA_DOCUMENTS
// reference endpoints that do not yet exist in ZX. They are mapped to their intended paths
// and will 404 until the backend endpoints are implemented.
const API_ENDPOINTS = {
  // ── ZX HDAPI endpoints (api/zxdata/) ──────────────────────────────────────────
  // Previously proxied through HD backend via *-zx/ paths; now called directly.
  // HD's ZXViewSetMixin rewrote pagination URLs and filtered attributes; ZX returns
  // correct pagination links and full attribute sets — no frontend adaptation needed.

  COMPANY_SECTORS: () => "api/zxdata/hd-sectors/",
  COI_FILINGS: () => "api/zxdata/hd-coi-filings/",
  STOCK_SPLITS: () => "api/zxdata/hd-stock-splits/",
  FUNDING_ROUNDS: () => "api/zxdata/hd-funding-rounds/",
  ENTITY_PROFILES: () => "api/zxdata/hd-entity-profiles/",
  INSIGHT_ARTICLE: () => "api/zxdata/hd-insight-articles/",
  TRANSACTION_RECORDS: () => "api/zxdata/hd-transaction-records/",
  TRANSACTION_RECORD_AGGREGATES: () => "api/zxdata/hd-transaction-record-aggregates/",
  COMPANY_LATEST_ORDER_FLOW: () => "api/zxdata/hd-company-latest-order-flow/",
  COMPANY_LATEST_ORDER_FLOW_REPORTS: () => "api/zxdata/hd-company-latest-order-flow-reports/",
  COMPANY_REPORTED_MARKS_VALUATION: id => `api/zxdata/hd-company-reported-marks-valuation/${id}/`,
  COMPANY_PRICE_VOLUME_ORDER_FLOW: () => "api/zxdata/hd-prices/",
  ZX_MARKET_INDEX_VALUES: () => "api/zxdata/hd-private-market-index-values/",
  COMPANY_AND_SECURITY_SEARCH: () => "api/zxdata/hd-all-symbols-search/",

  // Order flow reports — previously had company_id in URL path; now a query param.
  // HD backend also filtered JSON:API attributes; ZX returns full attribute set.
  COMPANY_VOLUME_TRADING_DATA: companyId =>
    `api/zxdata/hd-order-flow-reports/?company=${companyId}`,
  COMPANY_BID_OFFER_RATIO_DATA: companyId =>
    `api/zxdata/hd-order-flow-reports/?company=${companyId}`,
  HEADER_STATS: companyId => `api/zxdata/hd-order-flow-reports/?company=${companyId}`,

  COMPANY: (id = "") => (id ? `api/zxdata/hd-companies/${id}/` : "api/zxdata/hd-companies/"),
  GENERIC_COMPANY: () => "api/zxdata/company/search/",
  COMPANY_ARTICLES: id => `api/zxdata/hd-company-articles/?company=${id}`,
  CONTRARY_REPORTS: (id = "") => `api/zxdata/hd-contrary-reports/?company=${id}`,

  // TODO: no ZX endpoint yet — needs sacraservice ported (SACRA_URL/SACRA_API_KEY absent in ZX)
  SACRA_DOCUMENTS: (id = "") => `api/zxdata/hd-sacra-documents/?company=${id}`,

  // TODO: no ZX endpoint yet — needs KusanagiService ported to ZX
  DOWNLOAD_ZXDATA: companyId => `api/zxdata/orderflow/download-zxdata-report/${companyId}/`,

  // ── HD-specific endpoints (zxdata/) ─────────────────────────────────────
  // These are HD's own data endpoints, now served by ZX under /api/zxdata/.

  // accounts
  AGREEMENT: (id = "") =>
    id ? `api/zxdata/accounts/agreement/${id}/` : "api/zxdata/accounts/agreement/",
  SIGN_AGREEMENT: () => "api/zxdata/accounts/agreement/sign_agreement/",
  LOGIN: () => "api/zxdata/accounts/login/",
  LOGOUT: () => "api/zxdata/accounts/logout/",
  REFRESH: () => "api/zxdata/accounts/refresh/",
  TOKEN: () => "api/zxdata/accounts/token/",
  TOKEN_LOGIN: () => "api/zxdata/accounts/login/token/",
  USER: () => "api/zxdata/accounts/user/",
  WALKTHROUGH_PROGRESS: id => `api/zxdata/accounts/walkthrough_progress/${id}/`,
  CHANGE_PASSWORD: id => `api/zxdata/accounts/change_password/${id}/`,
  RESET_PASSWORD_SEND_EMAIL: () => "api/zxdata/accounts/password_reset/",
  RESET_PASSWORD_SET_PASSWORD: () => "api/zxdata/accounts/password_reset/confirm/",
  RESET_PASSWORD_NEW_USER: () => "api/zxdata/accounts/password_reset/new_user/",
  RESET_PASSWORD_VALIDATE_TOKEN: () => "api/zxdata/accounts/password_reset/validate_token/",
  CHANGE_ACCOUNT_SETTINGS: () => "api/zxdata/accounts/update_account_settings/",
  EMAIL_CHANGE_REQUEST: () => "api/zxdata/accounts/change_email/",
  UPDATED_AGREEMENT_FILES: () => "api/zxdata/accounts/updated_agreement_files/",

  // access
  HAS_FEATURE_ACCESS: () => "api/zxdata/access/has-feature-access/",
  HAS_PRO_DATA_ACCESS: companyId => `api/zxdata/access/has-pro-data-access/${companyId}/`,
  CAN_DOWNLOAD_ZX_DATA_REPORT: company_id =>
    `api/zxdata/access/can-download-zx-data-report/${company_id}/`,

  // analytics
  REPORT_ERROR: () => "api/zxdata/analytics/report-error/",
  MIXPANEL_TRACK: () => "api/zxdata/analytics/mixpanel/track/",
  MIXPANEL_SET_PEOPLE: () => "api/zxdata/analytics/mixpanel/set-people/",

  // portfolios
  PORTFOLIOS: () => "api/zxdata/portfolios/",
  PORTFOLIO_DETAIL: id => `api/zxdata/portfolios/${id}/`,
  DOWNLOAD_PORTFOLIO: id => `api/zxdata/portfolios/${id}/download/`,
  PORTFOLIO_COMPANIES: () => "api/zxdata/portfolios/companies/",
  SHARE_LOTS: () => "api/zxdata/portfolios/share-lots/",
  SHARE_LOT_DETAIL: id => `api/zxdata/portfolios/share-lots/${id}/`,

  // valuations
  CUSTOM_VALUATIONS: () => "api/zxdata/valuations/",

  // userprefs
  WATCHLIST_COMPANIES: () => "api/zxdata/userprefs/watchlist/",
};

// KN pagination query param
const KN_PAGE_QUERY_PARAM = "page[number]";

// pagination refetch buffer
const PAGINATION_ROW_COUNT_BUFFER = 20;

// feature names
const SACRA_FEATURE_NAME = "Sacra Documents";
const PRIVATE_MARKET_INDICES_FEATURE_NAME = "ZX Private Market Indices";
const EV_CALCULATOR_FEATURE_NAME = "EV Calculator";
const COMPANY_TRANSFERABILITY_FEATURE_NAME = "Company Transferability";
const CUSTOM_VALUATION_CALCULATOR_FEATURE_NAME = "Custom Valuation Calculator";
const PORTFOLIOS_FEATURE_NAME = "Portfolios";
const HOME_PAGE_FEATURE_NAME = "Home Page";
const CLOSED_TRANSACTIONS_FEATURE_NAME = "Closed transactions";

export {
  API_ENDPOINTS,
  KN_PAGE_QUERY_PARAM,
  PAGINATION_ROW_COUNT_BUFFER,
  SACRA_FEATURE_NAME,
  PRIVATE_MARKET_INDICES_FEATURE_NAME,
  EV_CALCULATOR_FEATURE_NAME,
  COMPANY_TRANSFERABILITY_FEATURE_NAME,
  CUSTOM_VALUATION_CALCULATOR_FEATURE_NAME,
  PORTFOLIOS_FEATURE_NAME,
  HOME_PAGE_FEATURE_NAME,
  CLOSED_TRANSACTIONS_FEATURE_NAME,
};
